
import classnames from 'classnames'
import { useMountEffect } from 'hooks/useMountEffect'
import * as React from 'react'
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { EMPTY, from as observableFrom } from 'rxjs'

// Interfaces
import {
  AnyContent,
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Gif,
  GIF_TYPE,
  LiveArticleData,
  Photo,
  PHOTO_TYPE,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { STOCK_GIF_TYPE, STOCK_PHOTO_TYPE, STOCK_VIDEO_TYPE } from 'interfaces/Content/StockContentTypes'

// Components
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import FetchArticleData from 'components/Fetch/FetchArticleData'
import ContentCardFooter from './ContentCardFooter'
import ContentCardMedia from './ContentCardMedia'
import ContentCardUpgradeOverlay from './ContentCardUpgradeOverlay'
import FavButton from './FavButton'
import ProgressMedia from './ProgressMedia'

// Services
import { addFavorite, addFavoriteLive, removeFavorite } from 'services/content/favorites'
import { favStateSelectorForItem } from 'services/content/selectors'
import { openContentPreviewWindow } from 'services/contentPreview'
import { checkFeatureAvailability, getProductUpgradeByFeature, promptUpgrade } from 'services/product'
import { FEATURE_FIND_FAVORITES } from 'shared/constants'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { useCardBlockedFeature } from './hooks/useCardBlockedFeature'

// Styles
import { promptCompose, setComposerContent, setComposerFile } from 'services/compose'
import { composerImageUrlsSelector, multiPostsArraySelector } from 'services/compose/selectors'
import CardStatusText from './CardStatusText'
import styles from './ContentCard.pcss'
import ContentCardActions, { ContentCardAction } from './ContentCardActions'
import ContentCardSelect from './ContentCardSelect'

const COMPOSE_BTN_ARTICLE_BOTTOM_OFFSET = 16
const RAISED_ATTRIBUTE = 'data-raised'
// EXPL: Long enough to outlast the card's shrink transition (--transition-duration-simple).
const RAISE_UNWIND_FALLBACK_TIMEOUT = 400
const GIF_CARD_TYPES = [PHOTO_TYPE, GIF_TYPE, STOCK_PHOTO_TYPE, STOCK_GIF_TYPE]
const VIDEO_CARD_TYPES = [VIDEO_TYPE, STOCK_VIDEO_TYPE]
const TALL_IMAGE_MAX_RATIO = 2.5
const WIDE_IMAGE_MIN_RATIO = 0.25

// Constants
const DEFAULT_ASPECT_RATIO = 0.5625
const DEFAULT_BASE_WIDTH = 264
const DEFAULT_BASE_WIDTH_SMALL = 232

export interface ContentCardProps {
  content: ContentItem
  studioButtonIcon?: React.ReactNode
  square?: boolean
  className?: string
  width?: number
  small?: boolean
  skipFeatureChecks?: boolean
  actionLabel?: React.ReactNode
  hideDetails?: boolean
  actions?: ContentCardAction[]

  onClick?(content: ContentItem): void
  onCompose?(content: ContentItem, aiWriter: boolean): void | boolean
  onSourceClick?(content: ContentItem, by: 'image' | 'title' | 'goto' | 'domain'): void
  onImageLoadingFailed?(content: ContentItem): void
  onLiveArticleDataFetched?(itemId: string, data: LiveArticleData): void
}

interface ContentCardState {
  loading: boolean
  aspectHeight: number
  aspectRatio: number

  liveArticleData?: LiveArticleData
}

type HasImage = ContentItem & { imageUrl: string }

// EXPL: `onSourceClick` only ever fires with 'image'. The title, domain and feed-name targets are
// plain anchors inside ContentCardFooter, so they navigate without notifying the callback.
const GIF_SPINNER_SIZE = 40
const GIF_SPINNER_SIZE_SMALL = 24

// EXPL: `baseCount` is the server's count, which already includes this user's favorite when
// `isFavorite` is set. Without that check, favoriting and then un-favoriting in the same session
// left `removed` set on an item the count never included, and the card showed one too few.
function getFavCount(baseCount: number, isFavorite: boolean, added: boolean, removed: boolean) {
  if (added && !isFavorite) {
    return baseCount + 1
  }

  if (removed && isFavorite && baseCount > 0) {
    return baseCount - 1
  }

  return baseCount
}

function setStateNotLoading(previousState: ContentCardState) { return { ...previousState, loading: false } }

export function ContentCard({
  content,
  width,
  small,
  square,
  skipFeatureChecks,
  className,
  actionLabel,
  hideDetails,
  actions,
  studioButtonIcon,
  onClick,
  onCompose,
  onSourceClick,
  onImageLoadingFailed,
  onLiveArticleDataFetched
}: ContentCardProps) {
  const favState = useSelector((state: StoreState) => favStateSelectorForItem(state, state, state, content.rawId, content.type))
  const dispatch = useDispatch<StoreThunkDispatch>()
  // EXPL: Composer state is only needed inside the create-post handler. Reading it through the store
  // instead of `useSelector` keeps every card in a grid from re-rendering on unrelated composer edits.
  const store = useStore<StoreState>()
  const imageUrl = content.type === STOCK_PHOTO_TYPE ? content.thumbnailUrl : content.imageUrl
  const cardElement = useRef<HTMLDivElement>(null)
  const footerElement = useRef<HTMLDivElement>(null)
  const composeBtnElement = useRef<HTMLSpanElement>(null)
  const isHovered = useRef(false)
  const unraiseTimeout = useRef<number | undefined>(undefined)

  const componentWidth = width || (small ? DEFAULT_BASE_WIDTH_SMALL : DEFAULT_BASE_WIDTH)
  const [state, setState] = useState<ContentCardState>({
    loading: content.type !== TEXT_TYPE,
    aspectHeight: componentWidth * DEFAULT_ASPECT_RATIO,
    aspectRatio: DEFAULT_ASPECT_RATIO
  })

  const isGifCard = content.type === GIF_TYPE || content.type === STOCK_GIF_TYPE
  const isArticleCard = content.type === ARTICLE_TYPE
  const isVideoCard = content.type === VIDEO_TYPE
  const isStockMedia = content.type === STOCK_PHOTO_TYPE || content.type === STOCK_GIF_TYPE || content.type === STOCK_VIDEO_TYPE
  // EXPL: `live` has to count as added on its own. Once the server answers, `replaceLiveFavorite`
  // re-keys `favorites.added` from `rawId` to the returned content-item id, so `favState.added`
  // goes false for a card that still looks itself up by `rawId` - only `favorites.live[rawId]`
  // survives the swap, and it is cleared again by `removeFavoriteImmediate`.
  const isFavoriteAdded = favState.added || Boolean(favState.live)
  const isFavorite = isFavoriteAdded || (Boolean(content.isFavorite) && !favState.removed)
  const favCount = getFavCount(content.favCount, Boolean(content.isFavorite), isFavoriteAdded, favState.removed)
  const blockedFeature = useCardBlockedFeature(content, skipFeatureChecks)

  const cardClassName = useMemo(() => classnames(
    styles.card,
    {
      [styles.square]: square,
      [styles.small]: small,
      [styles.loading]: state.loading,
      [styles.gif]: GIF_CARD_TYPES.includes(content.type),
      [styles.article]: content.type === ARTICLE_TYPE,
      [styles.video]: VIDEO_CARD_TYPES.includes(content.type),
      [styles.stock]: content.isStock,
      [styles.nodetails]: hideDetails,
      [styles.text]: content.type === TEXT_TYPE
    },
    className
  ), [content.type, content.isStock, square, small, hideDetails, className, state.loading])

  const cardStyle = useMemo(() => width ? { width } : undefined, [width])

  const cardImageClassName = useMemo(() => classnames(
    {
      [styles['tall-image']]: state.aspectRatio > TALL_IMAGE_MAX_RATIO,
      [styles['wide-image']]: state.aspectRatio < WIDE_IMAGE_MIN_RATIO,
      [styles.stretch]: state.aspectRatio >= 1
    }
  ), [state.aspectRatio])

  // To make response add to wrapper { paddingTop: `${this.state.aspectRatio * PERCENTS_IN_WHOLE}%` }
  const wrapperStyle = useMemo(
    () => state.loading ? { height: state.aspectHeight } : undefined,
    [state.loading, state.aspectHeight]
  )

  const getLiveContent = useCallback(() => {
    const liveData = state.liveArticleData

    if (!liveData) {
      return content
    }

    return {
      ...content,
      title: liveData.title,
      imageUrl: liveData.imageSrc,
      sourceLink: liveData.link
    }
  }, [content, state.liveArticleData])

  const liveContent = useMemo(getLiveContent, [getLiveContent])

  const setupComposer = useCallback((contentProp?: AnyContent) => {
    const data = contentProp || content as AnyContent

    if (data.type === STOCK_VIDEO_TYPE) {
      dispatch(setComposerFile({ url: (data as Video).videoUrl }))
      return
    }

    dispatch(setComposerContent({ content: data as ContentItem, sourceType: content.feed.type }))
  }, [dispatch, content])

  const onUpgrade = useCallback((feature: string) => {
    const product = dispatch(getProductUpgradeByFeature(feature))

    if (product) {
      dispatch(promptUpgrade({ feature, product: product.handles.annual }))
    }
  }, [dispatch])

  const checkIsContentBlocked = useCallback((aiWriter: boolean) => {
    let abortCompose: void | boolean = false

    if (onCompose) {
      abortCompose = onCompose(content, aiWriter)
    }

    return abortCompose
  }, [onCompose, content])

  const handleImageClick = useCallback(() => {
    onSourceClick?.(content, 'image')
  }, [onSourceClick, content])

  const handleClick = useCallback(() => {
    onClick?.(content)
  }, [onClick, content])

  const clearUnraiseTimeout = useCallback(() => {
    if (unraiseTimeout.current !== undefined) {
      window.clearTimeout(unraiseTimeout.current)
      unraiseTimeout.current = undefined
    }
  }, [])

  const onTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    const element = cardElement.current

    // EXPL: `transitionend` bubbles, so most of these come from the footer, title and compose
    // button. Only the card's own transition - which is transform, and nothing else - is the raise.
    if (!element || e.target !== element) {
      return
    }

    // EXPL: The grow and the shrink end on the same property, and the previous scale check read
    // both as collapsed (`--card-hover-scale` is 1.02, which `toFixed(1)` rounds to '1.0'), so the
    // raise was dropped the moment the card finished growing and a steadily hovered card fell back
    // under its later siblings. The pointer flag says which of the two just ended.
    if (isHovered.current) {
      return
    }

    clearUnraiseTimeout()
    element.removeAttribute(RAISED_ATTRIBUTE)
  }, [clearUnraiseTimeout])

  // EXPL: Hover styling itself is pure CSS (see `[data-card]:hover` rules). This only raises the
  // card's stacking order while it is hovered, which `onTransitionEnd` unwinds once it has shrunk
  // back. The raise is an attribute rather than a class because React owns this element's className
  // and rewrites it whenever `cardClassName` changes - e.g. when `loading` flips while the card is
  // hovered - which silently dropped an imperatively added class. React never touches an attribute
  // it does not render, so `data-raised` survives.
  const raiseCard = useCallback(() => {
    isHovered.current = true
    clearUnraiseTimeout()
    cardElement.current?.setAttribute(RAISED_ATTRIBUTE, '')
  }, [clearUnraiseTimeout])

  // EXPL: `transitionend` is the normal unwind, but it never fires when no transform transition
  // runs at all - reduced motion, or a hover too brief to start one. Because React never rewrites
  // `data-raised`, nothing else would ever clear it and the card would keep `z-index: 4` for good,
  // so leaving the card also arms a timer as the fallback.
  const unraiseCard = useCallback(() => {
    isHovered.current = false
    clearUnraiseTimeout()
    unraiseTimeout.current = window.setTimeout(() => {
      unraiseTimeout.current = undefined

      if (!isHovered.current) {
        cardElement.current?.removeAttribute(RAISED_ATTRIBUTE)
      }
    }, RAISE_UNWIND_FALLBACK_TIMEOUT)
  }, [clearUnraiseTimeout])

  useEffect(() => clearUnraiseTimeout, [clearUnraiseTimeout])

  const onLoadingDone = useCallback((loadedWidth = 0, loadedHeight = 0) => {
    if (!square && loadedWidth !== 0) {
      const aspectHeight = Math.round(componentWidth / loadedWidth * loadedHeight)
      const aspectRatio = aspectHeight / componentWidth
      setState(
        (previousState) => ({ ...previousState, aspectHeight, aspectRatio })
      )
    }

    setState(setStateNotLoading)
  }, [square, componentWidth])

  const onLoadingFailed = useCallback(() => {
    const aspectRatio = 1
    const aspectHeight = 0
    setState(
      (previousState) => ({ ...previousState, aspectHeight, aspectRatio })
    )
    setState(setStateNotLoading)

    if (content.type === PHOTO_TYPE && onImageLoadingFailed) {
      onImageLoadingFailed(content)
    }
  }, [content, onImageLoadingFailed])

  const onArticleDataFetch = useCallback((data: LiveArticleData) => {
    setState(prevState => ({ ...prevState, liveArticleData: data }))
    if (onLiveArticleDataFetched) {
      onLiveArticleDataFetched(content.id, data)
    }
  }, [content.id, onLiveArticleDataFetched])

  const onCreatePost = useCallback((skipAddToComposer?: boolean) => {
    // EXPL: Open prompt if composer already has carousel or multi-post
    const isPhoto = content.type === PHOTO_TYPE || content.type === STOCK_PHOTO_TYPE
    const composerState = store.getState()
    const composerCarouselImages = composerImageUrlsSelector(composerState)
    const multiPosts = multiPostsArraySelector(composerState)
    if (!isPhoto && composerCarouselImages.length > 0 || multiPosts.length > 0) {
      dispatch(promptCompose({ content, action: 'create' }))
      return true
    }
    const gifUrl = (content as Gif).gifUrl
    if (content.type === GIF_TYPE && gifUrl && gifUrl.toLowerCase().endsWith('.mp4')) {
      dispatch(setComposerFile({ url: gifUrl }))
    }
    if (!skipAddToComposer) {
      setupComposer(getLiveContent() as AnyContent)
    }
    return false
  }, [store, dispatch, content, setupComposer, getLiveContent])

  const handleAddFavorite = useCallback(() => {
    const favoritesAllowed = dispatch(checkFeatureAvailability(FEATURE_FIND_FAVORITES))

    if (favoritesAllowed) {
      return content.live
        ? observableFrom(dispatch(addFavoriteLive(getLiveContent() as any)).unwrap())
        : observableFrom(dispatch(addFavorite(getLiveContent())).unwrap())
    }

    return EMPTY
  }, [dispatch, content, getLiveContent])

  const handleRemoveFavorite = useCallback(() => {
    return observableFrom(dispatch(removeFavorite({ item: content, live: content.live })).unwrap())
  }, [dispatch, content])

  const handleOpenContentPreviewWindow = useCallback((c?: Video | Photo | Article | Gif) => {
    const contentData = c || content
    return dispatch(openContentPreviewWindow(contentData))
  }, [dispatch, content])

  useMountEffect(() => {
    if (state.loading) {
      const aspectHeight = componentWidth
      const aspectRatio = aspectHeight / componentWidth

      if (state.aspectHeight !== aspectHeight || state.aspectRatio !== aspectRatio) {
        setState(
          (previousState) => ({ ...previousState, aspectHeight, aspectRatio })
        )
      }
    }
  })

  // EXPL: The compose button on article/video cards sits just above the footer, so the footer has
  // to be measured. The footer grows on hover (`.card:hover .footer` adds 42px of top padding) -
  // the one state in which the button is actually visible - so a single un-hovered measurement is
  // always the wrong one, and the button rendered inside the taller footer, over the title. The
  // offset is written straight onto the button rather than fed back as state, because a
  // hover-driven `setState` would re-render the card on every pointer enter and leave.
  // `box: 'border-box'` is required: a padding change leaves the content box, which ResizeObserver
  // reports by default, untouched.
  useLayoutEffect(() => {
    const footer = footerElement.current

    if (!footer || (!isArticleCard && !isVideoCard)) {
      return
    }

    const updateComposeBottom = () => {
      const composeBtn = composeBtnElement.current

      if (composeBtn) {
        composeBtn.style.bottom = `${footer.offsetHeight + COMPOSE_BTN_ARTICLE_BOTTOM_OFFSET}px`
      }
    }

    updateComposeBottom()

    const observer = new ResizeObserver(updateComposeBottom)
    observer.observe(footer, { box: 'border-box' })

    return () => observer.disconnect()
  }, [isArticleCard, isVideoCard, state.loading, blockedFeature])

  const gifLoader = useMemo(() => isGifCard
    ? (
      <div className={styles['gif-loader']} data-testid="gif-card-loader">
        <CircularProgress size={small ? GIF_SPINNER_SIZE_SMALL : GIF_SPINNER_SIZE} />
      </div>
    )
    : undefined, [isGifCard, small])

  const upgradeOverlayFavButton = useMemo(() => isFavorite
    ? (
      <FavButton
        isFavorite={isFavorite}
        expandOnCardHover
        count={favCount}
        className={classnames(
          styles['btn-favorite'],
          styles['btn-favorite-upgrade-overlay'],
          { [styles['fav-visible']]: !state.loading }
        )}
        onFavorite={handleAddFavorite}
        onUnfavorite={handleRemoveFavorite}
      />
    )
    : null, [isFavorite, favCount, state.loading, handleAddFavorite, handleRemoveFavorite])

  const fetchLiveData = (content.type === ARTICLE_TYPE && (content as Article).scrape)

  return (
    <Paper
      ref={cardElement}
      className={cardClassName}
      onMouseEnter={raiseCard}
      onMouseLeave={unraiseCard}
      onTransitionEnd={onTransitionEnd}
      style={cardStyle}
      data-testid="content-card"
      data-card=""
      data-contenttype={content.type}
      onClick={handleClick}
    >
      {blockedFeature && (
        <ContentCardUpgradeOverlay
          key="upgrade-overlay"
          feature={blockedFeature}
          contentType={liveContent.type}
          imageUrl={(liveContent as any).imageUrl}
          className={styles['upgrade-overlay']}
          onUpgrade={onUpgrade}
          favButton={upgradeOverlayFavButton}
        />
      )}
      {content.type !== TEXT_TYPE && (!actions || actions.includes('select')) && (
        <ContentCardSelect content={content} getSelectableContent={getLiveContent} />
      )}
      <ContentCardActions
        content={liveContent}
        favCount={favCount}
        isFavButtonVisible={!state.loading}
        isStock={content.isStock}
        isFavorite={isFavorite}
        isBlocked={Boolean(blockedFeature)}
        composeBtnRef={composeBtnElement}
        skipFeatureChecks={skipFeatureChecks}
        className={styles.actions}
        createPostLabel={actionLabel}
        actions={actions}
        studioButtonIcon={studioButtonIcon}
        onAddFavorite={handleAddFavorite}
        onRemoveFavorite={handleRemoveFavorite}
        isContentBlocked={checkIsContentBlocked}
        onCreatePost={onCreatePost}
      />

      <div className={styles.content} style={wrapperStyle}>
        {content.type !== TEXT_TYPE && (
          state.loading ? (
            <ProgressMedia
              image={imageUrl || null}
              onDone={onLoadingDone}
              onError={onLoadingFailed}
              className={styles.progress}
              loader={gifLoader}
            />
          ) : (
            <React.Fragment>
              {state.aspectHeight !== 0 && (liveContent as any).imageUrl ? (
                <ContentCardMedia
                  content={liveContent as HasImage}
                  className={cardImageClassName}
                  small={small}
                  hideProvider={hideDetails}
                  onClick={handleImageClick}
                  openContentPreviewWindow={handleOpenContentPreviewWindow}
                />
              ) : (
                <div className={styles['no-image-box']}>
                  <BrokenImageIcon className={styles['icon-no-image']} />
                </div>
              )}
              {(!liveContent.isStock || isStockMedia) && <ContentCardFooter ref={footerElement} content={liveContent} />}
            </React.Fragment>
          )
        )}
        {content.type === TEXT_TYPE && (
          <React.Fragment>
            <CardStatusText text={content.status || ''} small={small} onClick={handleOpenContentPreviewWindow} />
            <ContentCardFooter content={liveContent} />
          </React.Fragment>
        )}
      </div>

      {fetchLiveData && (
        <FetchArticleData
          key="live-fetch"
          url={(content as Article).sourceLink || (content as Article).socialLink}
          onFetched={onArticleDataFetch}
        />
      )}
    </Paper>
  )
}

export default memo(ContentCard)
